import type { SegmentUnits, StevedoreSession } from "../../../../backend/shared";

// Canonical cargo-segment codes — same order as the tooltip's
// per-segment cargo lines (voyage-mapper SEGMENT_ORDER).
export type SegmentCode = "C" | "HH" | "RT" | "BB" | "NCC";
export const SEGMENT_CODES: readonly SegmentCode[] = ["C", "HH", "RT", "BB", "NCC"];

// In-flight UI state for a single side's segment grid (load / discharge /
// shifting). "" = empty cell; a number = either the upstream default
// pre-fill OR a user-entered override. The two are distinguished by
// comparison against the upstream defaults map; see segIsAtDefault.
export type SegmentState = Record<SegmentCode, number | "">;
export const EMPTY_SEGMENT_STATE: SegmentState = { C: "", HH: "", RT: "", BB: "", NCC: "" };

// Seed an editor row's SegmentState from the saved override (winning)
// then from the upstream cargo default. Pre-filling makes the native
// spinner / arrow keys count up/down from a real number on the very
// first interaction — no double-click to focus-then-increment. Save-
// time filtering (segStateToSegments) drops cells still equal to the
// default so we don't persist a "no-op" override row.
export function segStateFrom(
  saved: SegmentUnits | null | undefined,
  defaults: SegmentUnits | null | undefined,
): SegmentState {
  const out: SegmentState = { ...EMPTY_SEGMENT_STATE };
  for (const code of SEGMENT_CODES) {
    const sv = saved?.[code];
    if (typeof sv === "number" && Number.isFinite(sv)) {
      out[code] = sv;
      continue;
    }
    const dv = defaults?.[code];
    if (typeof dv === "number" && Number.isFinite(dv) && dv !== 0) {
      out[code] = dv;
    }
  }
  return out;
}

// `true` when the input's current state equals the upstream default
// — mirrors the stevedore-slider's "muted when same as default, black
// when deviated" cue so the user can tell at a glance which cells
// have a real override.
export function segIsAtDefault(
  state: SegmentState,
  defaults: SegmentUnits | null | undefined,
  code: SegmentCode,
): boolean {
  const v = state[code];
  if (v === "") return true;
  const def = defaults?.[code];
  return typeof def === "number" && def === v;
}

// `true` if ANY segment in the given row has a real override (cell
// value differs from its upstream default). Drives the row label's
// muted-vs-primary colour.
export function rowHasOverride(state: SegmentState, defaults: SegmentUnits | null | undefined): boolean {
  for (const code of SEGMENT_CODES) {
    if (!segIsAtDefault(state, defaults, code)) return true;
  }
  return false;
}

// `true` if ANY of the three sides (load / disch / shift) has an
// override in the given segment column. Drives the column header's
// muted-vs-primary colour.
export function colHasOverride(
  code: SegmentCode,
  loadState: SegmentState, loadDefaults: SegmentUnits | null | undefined,
  dischState: SegmentState, dischDefaults: SegmentUnits | null | undefined,
  shiftState: SegmentState, shiftDefaults: SegmentUnits | null | undefined,
): boolean {
  return !segIsAtDefault(loadState, loadDefaults, code)
    || !segIsAtDefault(dischState, dischDefaults, code)
    || !segIsAtDefault(shiftState, shiftDefaults, code);
}

// Collapse the in-flight SegmentState back into a SegmentUnits map for
// persistence. Cells whose value matches the upstream default are
// dropped — keeping them would freeze the value against future
// upstream cargo updates. Returns null when no cell remains, signaling
// "no override row to persist for this side".
export function segStateToSegments(
  state: SegmentState,
  defaults: SegmentUnits | null | undefined,
): SegmentUnits | null {
  let any = false;
  const out: SegmentUnits = {};
  for (const code of SEGMENT_CODES) {
    const v = state[code];
    if (v === "" || !Number.isFinite(v)) continue;
    const def = defaults?.[code];
    if (typeof def === "number" && def === v) continue;
    out[code] = v as number;
    any = true;
  }
  return any ? out : null;
}

// Resolve a raw <input> string into the next SegmentState cell value
// for one code. Three cases:
//   1. raw === ""           → user cleared the cell; restore the
//                             upstream default if there is one, else
//                             leave empty.
//   2. raw === "0" + default → keep 0 in state. The user is explicitly
//                             overriding the upstream value with zero
//                             (e.g. "no discharge here even though the
//                             cargo plan says 100").
//   3. raw === "0" + no default → collapse to "" — there's no upstream
//                             value to override, so 0 is just empty.
// Non-numeric raw input collapses to "".
export function nextSegValue(
  raw: string,
  defaults: SegmentUnits | null | undefined,
  code: SegmentCode,
): number | "" {
  const def = defaults?.[code];
  const hasDefault = typeof def === "number" && Number.isFinite(def) && def !== 0;
  if (raw === "") return hasDefault ? def : "";
  const num = Number(raw);
  if (!Number.isFinite(num)) return "";
  if (num === 0 && !hasDefault) return "";
  return num;
}

// Shift a YYYY-MM-DD date by N units of the chosen field. Returns input
// unchanged if it's empty or unparseable. Day / month / year deltas are
// applied via the Date object so month- and year-boundary arithmetic
// (incl. clamping Jan 31 + 1 month → Feb 28/29) is handled correctly.
export function bumpDateField(
  yyyymmdd: string,
  delta: number,
  field: "day" | "month" | "year",
): string {
  if (!yyyymmdd) return yyyymmdd;
  const next = new Date(`${yyyymmdd}T00:00`);
  if (Number.isNaN(next.getTime())) return yyyymmdd;
  switch (field) {
    case "day":   next.setDate(next.getDate() + delta); break;
    case "month": next.setMonth(next.getMonth() + delta); break;
    case "year":  next.setFullYear(next.getFullYear() + delta); break;
  }
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
}

// Day-only convenience — used by the time wheel handler when an hour
// rollover crosses midnight.
export function bumpDate(yyyymmdd: string, delta: number): string {
  return bumpDateField(yyyymmdd, delta, "day");
}

// Apply a delta to either the hour or minute portion of an HH:MM time.
// Minute overflow cascades to hour; hour overflow rolls to the next/prev
// calendar day (the caller bumps the date by `dayDelta`). Empty / partial
// input is treated as "00:00" so the wheel always produces a valid time.
export function bumpTime(
  hhmm: string,
  delta: number,
  side: "hour" | "minute",
): { time: string; dayDelta: number } {
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(hhmm);
  const hh0 = m ? Math.min(23, Math.max(0, parseInt(m[1]!, 10))) : 0;
  const mm0 = m ? Math.min(59, Math.max(0, parseInt(m[2]!, 10))) : 0;
  const totalMin = side === "hour"
    ? hh0 * 60 + mm0 + delta * 60
    : hh0 * 60 + mm0 + delta;
  const dayMin = 24 * 60;
  const dayDelta = Math.floor(totalMin / dayMin);
  const wrapped = ((totalMin % dayMin) + dayMin) % dayMin;
  const hh = Math.floor(wrapped / 60);
  const mm = wrapped % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  return { time: `${pad(hh)}:${pad(mm)}`, dayDelta };
}

// Wheel-step accumulator — folds multiple small wheel events (typical of
// trackpads) into discrete ±1 steps once the accumulated deltaY crosses
// the threshold. Returns +1 (scroll up = increase), -1 (scroll down =
// decrease), or 0 (no step yet — keep accumulating). State is per DOM
// element so two inputs scroll independently without leaking deltas.
// Resets to 0 on each emitted step so a single 'big' wheel event yields
// one step (not several), matching the user-expected one-click idiom.
export function createWheelStepper(threshold: number = 50) {
  const acc = new WeakMap<HTMLElement, number>();
  return function step(el: HTMLElement, deltaY: number): -1 | 0 | 1 {
    const next = (acc.get(el) ?? 0) + deltaY;
    if (next >= threshold) {
      acc.set(el, 0);
      return -1;
    }
    if (next <= -threshold) {
      acc.set(el, 0);
      return 1;
    }
    acc.set(el, next);
    return 0;
  };
}

// Resolve a cursor's horizontal position within a centre-aligned mono
// text input to the calendar field underneath. The text "DD-MM-YYYY"
// is 10 chars; with text-align: center the visible chars sit
// symmetrically around the input centre regardless of any flex-grown
// slack. Char positions (from centre, in char widths):
//   D D - M M - Y Y Y Y
//   -4.5 -3.5 -2.5 -1.5 -0.5 +0.5 +1.5 +2.5 +3.5 +4.5
// So the first dash sits at -2.5*charW (day/month boundary) and the
// second dash at +0.5*charW (month/year boundary). Pass the cursor's
// pixel offset relative to the input's left edge, the input's pixel
// width, and the monospace character advance (px).
export function dateFieldAtOffset(
  offsetX: number,
  width: number,
  charW: number,
): "day" | "month" | "year" {
  if (width <= 0 || charW <= 0) return "day";
  const rel = offsetX - width / 2;
  if (rel < -2.5 * charW) return "day";
  if (rel <  0.5 * charW) return "month";
  return "year";
}

// "HH:MM" — the colon sits at the input centre, so left-of-centre is
// the hour and right-of-centre is the minute. charW is ignored (kept
// for symmetry with dateFieldAtOffset's signature) when the format is
// inherently symmetric.
export function timeFieldAtOffset(offsetX: number, width: number): "hour" | "minute" {
  return offsetX < width / 2 ? "hour" : "minute";
}

// Measure the pixel advance of a single "0" character in the given
// CSS font shorthand. Cached per shorthand string so repeated lookups
// during wheel events stay cheap. Falls back to 0.6 × font-size in
// non-DOM environments (tests).
const charWidthCache = new Map<string, number>();
export function measureCharWidth(fontShorthand: string): number {
  const cached = charWidthCache.get(fontShorthand);
  if (cached !== undefined) return cached;
  if (typeof document === "undefined") {
    const fallback = 8.16;
    charWidthCache.set(fontShorthand, fallback);
    return fallback;
  }
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return 8.16;
  ctx.font = fontShorthand;
  const w = ctx.measureText("0").width;
  charWidthCache.set(fontShorthand, w);
  return w;
}

// Detect the first pair of overlapping stevedore working sessions,
// returning their indices in the original input order so the caller can
// surface an error against the user-visible row numbers. Touching
// ranges (next.start === prev.end) are NOT counted as overlap — shift
// handovers commonly happen at the minute boundary. Returns null when
// no overlap exists.
export function findOverlap(
  sessions: ReadonlyArray<{ start: string; end: string }>,
): { firstIndex: number; secondIndex: number } | null {
  const indexed = sessions.map((s, i) => ({
    idx: i,
    startMs: Date.parse(s.start),
    endMs: Date.parse(s.end),
  })).filter((s) => Number.isFinite(s.startMs) && Number.isFinite(s.endMs));
  indexed.sort((a, b) => a.startMs - b.startMs);
  for (let i = 1; i < indexed.length; i++) {
    const prev = indexed[i - 1]!;
    const cur = indexed[i]!;
    if (cur.startMs < prev.endMs) {
      return {
        firstIndex: Math.min(prev.idx, cur.idx),
        secondIndex: Math.max(prev.idx, cur.idx),
      };
    }
  }
  return null;
}

// Draft row as edited in the StopOverrideDialog session grid.
export interface SessionDraftInput {
  startDate: string;  // YYYY-MM-DD
  startTime: string;  // HH:MM
  endDate: string;
  endTime: string;
}

// Validate the dialog's draft session rows into a persistable array.
// Empty-both rows skip silently; a partial fill, an unparseable range,
// end <= start, or an overlap surface an error. Error strings reference
// the on-screen row number (1-indexed over the ORIGINAL drafts, so a
// skipped empty row still counts toward the number the user sees). A
// null `sessions` result means "nothing to persist".
export function validateSessions(
  drafts: readonly SessionDraftInput[],
): { ok: true; sessions: StevedoreSession[] | null } | { ok: false; error: string } {
  if (drafts.length === 0) return { ok: true, sessions: null };
  const validated: { rowIdx: number; session: StevedoreSession }[] = [];
  for (let i = 0; i < drafts.length; i++) {
    const d = drafts[i]!;
    const startFilled = !!(d.startDate && d.startTime);
    const endFilled = !!(d.endDate && d.endTime);
    if (!startFilled && !endFilled) continue;
    if (!startFilled || !endFilled) {
      return { ok: false, error: `Session ${i + 1}: both start and end (date + time) are required.` };
    }
    const startISO = `${d.startDate}T${d.startTime}`;
    const endISO = `${d.endDate}T${d.endTime}`;
    const startMs = Date.parse(startISO);
    const endMs = Date.parse(endISO);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      return { ok: false, error: `Session ${i + 1}: ${startISO} → ${endISO} is not a valid range.` };
    }
    if (endMs <= startMs) {
      return { ok: false, error: `Session ${i + 1}: end must be after start.` };
    }
    validated.push({ rowIdx: i, session: { start: startISO, end: endISO } });
  }
  const overlap = findOverlap(validated.map((v) => v.session));
  if (overlap) {
    const a = validated[overlap.firstIndex]!.rowIdx + 1;
    const b = validated[overlap.secondIndex]!.rowIdx + 1;
    return { ok: false, error: `Sessions ${a} and ${b} overlap — working times can't share time.` };
  }
  return { ok: true, sessions: validated.length > 0 ? validated.map((v) => v.session) : null };
}
